<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, download_file, buildPageFooter, isButtonDisabled } from '../../functionCollection.js';

    const props = defineProps(['fetchUrl', 'userNumber', 'maxInvoices']);

    const invoiceData = ref([]);

    // pagination
    const currentPage = ref(1); // current page, needed for the page footer. Initially page 1.
    // const maxPageNumber = Math.ceil(props.maxInvoices/20); // 20 entries per page, fixed
    const maxPageNumber = ref(1); // should be gotten dynamically on first call through invoiceData.value.meta.last_page. reference AuthMediaList.vue
    const invoicesOnPage = ref(null); // how many invoices are on the page
    const footerPageNumbers = ref(new Array()); //array for building page selection footer
    const renderFooter = ref(true); //if footer should be rendered or not

    //for changing the "show all" toggle dynamically
    const faClass = ref("fa-solid fa-lg fa-toggle-");
    const toggleText = ref("off");
    const faToggle = ref(faClass.value.concat(toggleText.value));

    const selected = ref([]); // array of currently selected invoices
    const bigChecked = ref(false); // if the big checkmark to select all on page is checked or not
    const counter = ref(0);

    // we initially get our data, set the number of pages there are, and how many invoices we render this time.
    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl, props.userNumber);
        invoiceData.value = await getFromAPI(url);
        if (invoiceData.value.data !== undefined && invoiceData.value.data.length > 0) {
            if (toggleText.value === "off") {
                // when we get all pages at once, no meta data is sent at all
                maxPageNumber.value = invoiceData.value.meta.last_page;
            }
            invoiceData.value = invoiceData.value.data;
            invoicesOnPage.value = invoiceData.value.length;

            footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
        } else {
            renderFooter.value = false;
            invoiceData.value = invoiceData.value.data;
        }
    })

    // if we're supposed to render all invoices at once, the footer is not rendered, so we remove the "per_page" modifier then when getting data.
    function getFullUrl(url) {
        // return url.concat('/').concat(props.userNumber).concat(`?page=${currentPage.value}&columns=${columnsToFetch}&column=Teilnehmer&order=asc&per_page=10`);
        let newUrl = url;
        if (props.userNumber !== '') {
            newUrl = newUrl.concat('/').concat(props.userNumber);
        }
        if (renderFooter.value === true) {
            return newUrl.concat(`?page=${currentPage.value}&per_page=20&column=Rechnungsdatum&order=desc`);
        } else {
            return newUrl.concat(`?page=${currentPage.value}&column=Rechnungsdatum&order=desc`);
        }
    }

    // selects all invoices currently rendered on the page. Different from selecting all existing invoices!
    async function checkAllOnPage() {
        // checkbox set to false: drop all selected values.
        if (bigChecked.value === false) {
            selected.value = [];
            counter.value = 0;
            return;
        }
        // checkbox set to true: loop through and check all checkboxes.
        for (let i = 0; i < invoicesOnPage.value; i++) {
            if (!(selected.value.includes(invoiceData.value[i].Rechnungsnummer))) {
                selected.value.push(invoiceData.value[i].Rechnungsnummer);
            }
        }
        counter.value = selected.value.length;
    }

    // checks or unchecks big "select all" checkmark when (un)checking individual invoices.
    // could potentially be cleaned up to not use a counter; that counter comes from testing
    // things when I still mistakenly used @click instead of @change to call function.
    function uncheckBigCheckmark(invoiceID) {
        if (selected.value.includes(invoiceID) === true) {
            counter.value++;
        } else if (selected.value.includes(invoiceID) === false) {
            counter.value--;
        }

        if (counter.value >= invoicesOnPage.value) {
            bigChecked.value = true;
        } else {
            bigChecked.value = false;
        }
    }

    // selects absolutely all invoices: switches on the toggle to show all existing invoices, then selects them all.
    async function selectAllInvoices() {
        await setToggle();
        bigChecked.value = true;
        await checkAllOnPage();
    }

    // cancels the entire selection, then calls the very first page, just like a reload.
    async function resetSelection() {
        await setToggle();
        counter.value = 0;
    }

    // for Alle anzeigen, sets the toggle's FontAwesome class and gets the needed invoices again
    async function setToggle() {
        if (toggleText.value === "off") {
            toggleText.value = "on";
            faToggle.value = faClass.value.concat(toggleText.value);
            renderFooter.value = false;
            await getNewPage(0); //0 behaves the same as 1, but is meant as a hint in the code that no actual footer is rendered
        } else if (toggleText.value === "on") {
            toggleText.value = "off";
            faToggle.value = faClass.value.concat(toggleText.value);
            renderFooter.value = true;
            await getNewPage(1);
        } else {
            toggleText.value = "off";
        }
    }

    // calls new Teilnehmer info whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        invoiceData.value = await getFromAPI(getFullUrl(props.fetchUrl, props.userNumber));
        // maxPageNumber.value = invoiceData.value.meta.last_page;
        invoiceData.value = invoiceData.value.data;
        invoicesOnPage.value = invoiceData.value.length;
        bigChecked.value = false;
        selected.value = [];
        footerPageNumbers.value = buildPageFooter();
    }

    // collection of API calls (and corresponding URLs) to open or download PDF/download CSV files of invoices.
    function getPDFFromAPI(invoiceID) {
        let url = "";
        if (props.userNumber !== '') {
            url = `api/rechnung/${props.userNumber}/${invoiceID}/pdf`;
        } else {
            url = `api/rechnung/${invoiceID}/pdf`;
        }
        window.open(url, '_blank');
    }
    function downloadPDFFromAPI(invoiceID) {
        let url = "";
        if (props.userNumber !== '') {
            url = `api/rechnung/${props.userNumber}/${invoiceID}/pdf`;
        } else {
            url = `api/rechnung/${invoiceID}/pdf`;
        }
        download_file(url);
    }
    function downloadCSVFromAPI(invoiceID) {
        let url = "";
        if (props.userNumber !== '') {
            url = `api/rechnung/${props.userNumber}/${invoiceID}/csv`;
        } else {
            url = `api/rechnung/${invoiceID}/csv`;
        }

        download_file(url);
    }

    // works with the selected array to download a .zip of several invoices (PDFs and CSVs in the same .zip)
    function downloadZIPFromAPI() {
        let selectedAsString = selected.value.toString();
        let url = "";
        if (props.userNumber !== '') {
            url = `api/rechnungen/${props.userNumber}/${selectedAsString}/zip`;
        } else {
            url = `api/rechnungen/${selectedAsString}/zip`;
        }
        download_file(url);
    }

</script>

<template>

    <div class="table-header" v-if="((typeof invoiceData !== 'undefined') && (invoiceData.length > 0))">
        <div class="actions">
            <div class="checkbox-container">
                <input type="checkbox" v-model="bigChecked" @change="checkAllOnPage()">
                <div class="check"></div>
            </div>
            <div class="button-container">
                <button
                    class="btn btn-link header-button disabled"
                    v-if="!((Array.isArray(selected) && selected.length))">
                    <i class="fa-solid fa-lg fa-box-archive"></i>
                </button>
                <button
                    class="btn btn-link header-button"
                    v-if="(Array.isArray(selected) && selected.length)"
                    v-tooltip="{ content: (bigChecked ? 'Alle ' : 'Ausgewählte') + ' Rechnungen herunterladen'}">
                    <i class="fa-solid fa-lg fa-box-archive" @click="downloadZIPFromAPI()"></i>
                </button>
            </div>
        </div>
        <button class="btn btn-link footer-button" @click="setToggle()">
            Alle anzeigen
            <i :class="faToggle"></i>
        </button>
    </div>
    <div class="main-table" v-if="invoiceData !== null && invoiceData.length > 0">
        <div class="selectAll" v-if="bigChecked === true">
            <span v-if="selected.length === invoicesOnPage && props.maxInvoices != invoicesOnPage">
                Alle {{ invoicesOnPage }} Rechnungen auf dieser Seite sind ausgewählt.
            </span>
            <span v-if="selected.length === invoicesOnPage && selected.length != props.maxInvoices && props.maxInvoices != invoicesOnPage"
                class="btn btn-link" @click="selectAllInvoices()">
                Alle {{ props.maxInvoices }} Rechnungen auswählen
            </span>
            <span v-if="selected.length == props.maxInvoices && props.maxInvoices == invoicesOnPage">
                Alle {{ invoicesOnPage }} Rechnungen sind ausgewählt.
            </span>
            <span v-if="selected.length == props.maxInvoices && props.maxInvoices == invoicesOnPage"
                class="btn btn-link" @click="resetSelection()">
                Auswahl aufheben
            </span>
        </div>

        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head"></th>
                        <th class="table-head">Rechnung</th>
                        <th class="table-head">Datum</th>
                        <th class="table-head">Betrag</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="invoice of invoiceData" :key="invoice.id">
                        <td style="position: relative">
                            <input
                                type="checkbox"
                                :id="invoice.Rechnungsnummer"
                                :value="invoice.Rechnungsnummer"
                                v-model="selected"
                                @change="uncheckBigCheckmark(invoice.Rechnungsnummer)">
                            <div class="check"></div>
                        </td>
                        <td>{{ invoice.Rechnungsnummer }}</td>
                        <td>{{ invoice.Rechnungsdatum }}</td>
                        <td>{{ invoice.Betrag }}</td>
                        <td>
                            <i class="fa-solid fa-eye fa-lg" @click="getPDFFromAPI(invoice.Rechnungsnummer)"></i>
                        </td>
                        <td>
                            <i class="fa-solid fa-file-pdf fa-lg" @click="downloadPDFFromAPI(invoice.Rechnungsnummer)"></i>
                        </td>
                        <td>
                            <i class="fa-solid fa-file-csv fa-lg" @click="downloadCSVFromAPI(invoice.Rechnungsnummer)"></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="renderFooter === true">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start', currentPage, maxPageNumber)" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page, currentPage, maxPageNumber)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end', currentPage, maxPageNumber)">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="(invoiceData.length === 0)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine Rechnungen gefunden.</span>
        </div>
    </div>

</template>
